import { useQuery } from '@tanstack/react-query';

import { getCatalogCategoriesQuery } from 'Services';
import { Category } from 'Services/Mongo/Types';

export const useCategories = () => {
  const { data, isLoading, isFetching, isInitialLoading } = useQuery({
    ...getCatalogCategoriesQuery(),
    select: (response: any) => response?.categories,
    staleTime: 300000
  });

  /**
   * Returns a new tree with only the nodes that either directly match the search term
   * (case‑insensitively) or are on the direct path from a top‑level node to a matching node.
   */
  function searchCategories(
    categories: Category[],
    searchTerm: string
  ): Category[] {
    const term = searchTerm.toLowerCase().trim();

    // This array will hold each path (an array of nodes) from the root to a node that directly matches.
    const matchingPaths: Category[][] = [];

    // A recursive DFS that collects paths where a node's name directly includes the search term.
    function dfs(node: Category, path: Category[]) {
      const newPath = path?.length ? [...path, node] : [node];
      if (node?.name?.toLowerCase().includes(term)) {
        matchingPaths?.push(newPath);
        // Note: if you want to stop searching further down a branch once a match is found,
        // you could return here. But if you want to collect all matches even under a matching node,
        // do not return.
      }
      if (node?.children?.length) {
        for (const child of node?.children) {
          dfs(child, newPath);
        }
      }
    }

    // Start DFS for every top-level category.

    if (categories?.length) {
      for (const cat of categories) {
        dfs(cat, []);
      }
    }
    // Now, merge the collected paths into a single pruned tree.
    // Each path is an array of nodes (from root to matching node). We want to “stitch”
    // together those paths so that each node appears only once and its children array
    // only contains nodes that are on some matching path.
    const prunedTree: Category[] = [];

    // This helper inserts a full path into the tree.
    function insertPath(path: Category[]) {
      let currentLevel = prunedTree;
      for (const node of path) {
        // See if a node with the same _id already exists at this level.
        let existing = currentLevel?.find(n => n._id === node._id);
        if (!existing) {
          // Create a shallow copy of the node with an empty children array.
          existing = { ...node, children: [] };
          currentLevel?.push(existing);
        }
        // Move to the children array for the next node in the path.
        currentLevel = existing.children;
      }
    }

    // Insert each matching path into the prunedTree.
    for (const path of matchingPaths) {
      insertPath(path);
    }

    return prunedTree;
  }

  return {
    isLoading,
    isFetching,
    isInitialLoading,
    categories: data || [],

    searchCategories
  };
};
